import { useEffect, useRef, useState } from 'react';
import './BuzzerStatus.css';

const TEAM_NAMES = ['Team One', 'Team Two', 'Team Three'];
const TEAM_COLORS = ['#1565c0', '#b71c1c', '#2e7d32'];
const BUZZ_SECONDS = 5;

export interface BuzzerState {
  armed: boolean;
  activeBuzz: { teamIndex: number; at: number; playerName?: string | null } | null;
  wrongTeams: number[];
  answered: boolean;
  ddShown?: boolean;
}

interface Props {
  buzzer: BuzzerState;
  answer: string;
  onBuzzResult?: (correct: boolean) => void;
}

export default function BuzzerStatus({ buzzer }: Props) {
  const { armed, activeBuzz, wrongTeams, answered } = buzzer;
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timesUpRef = useRef<HTMLAudioElement>(null);
  const timesUpPlayedRef = useRef(false);

  useEffect(() => {
    if (activeBuzz) {
      timerRef.current = setInterval(() => setNow(Date.now()), 100);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeBuzz?.at]);

  // Reset the played flag when a new buzz starts, so each buzz can play once.
  useEffect(() => { timesUpPlayedRef.current = false; }, [activeBuzz?.at]);

  if (!activeBuzz && wrongTeams.length === 0 && !armed) return null;

  const activeColor = activeBuzz ? (TEAM_COLORS[activeBuzz.teamIndex] ?? '#1565c0') : null;
  const timeLeft = activeBuzz ? Math.max(0, BUZZ_SECONDS - (now - activeBuzz.at) / 1000) : null;
  const timerExpired = timeLeft !== null && timeLeft <= 0;

  // Play the times-up beep once when the timer first reaches 0.
  if (timerExpired && !timesUpPlayedRef.current && timesUpRef.current) {
    timesUpRef.current.currentTime = 0;
    timesUpRef.current.volume = 0.7;
    timesUpRef.current.play().catch(() => {});
    timesUpPlayedRef.current = true;
  }

  return (
    <div className="bsb-bar">
      {armed && !activeBuzz && !answered && (
        <div className="bsb-armed">🔔 Buzzers Armed</div>
      )}

      {activeBuzz && (
        <div className="bsb-active" style={{ '--buzz-color': activeColor } as any}>
          <span className="bsb-team-label">
            {activeBuzz.playerName
              ? `${activeBuzz.playerName} (${TEAM_NAMES[activeBuzz.teamIndex] ?? `Team ${activeBuzz.teamIndex + 1}`}) BUZZED IN`
              : `${TEAM_NAMES[activeBuzz.teamIndex] ?? `Team ${activeBuzz.teamIndex + 1}`} BUZZED IN`}
          </span>
          {timeLeft !== null && (
            <span className={`bsb-timer ${timerExpired ? 'bsb-timer-expired' : timeLeft < 2.5 ? 'bsb-timer-urgent' : ''}`}>
              {timerExpired ? "TIME'S UP" : `${timeLeft.toFixed(1)}s`}
            </span>
          )}
        </div>
      )}

      {wrongTeams.length > 0 && !activeBuzz && !answered && (
        <div className="bsb-locked">
          Locked out: {wrongTeams.map(i => TEAM_NAMES[i] ?? `Team ${i + 1}`).join(', ')}
        </div>
      )}
      <audio ref={timesUpRef} src="/sounds/times-up.mp3" preload="auto" />
    </div>
  );
}
